/**
 * RouteScene — today's populated expedition. RegionMap -> *Route* -> (Battle | Town).
 *
 * A calm overworld the player strolls through with the lead Pokémon following a step
 * behind. The day's wild encounters are pre-rolled by core/encounter.generateRoute —
 * a pure function of the session's `hash(worldSeed + inGameDate + nodeId)` — and
 * placed as VISIBLE, approachable creatures milling about. Walk up to one to start a
 * battle (it's handed off to the Battle scene); sneaking up from behind grants a calm
 * "you got the jump" first-strike flag. Occasionally a simulated trainer wanders here
 * too — greet them, spar a no-stakes friendly battle, or propose a trade. A soft
 * "head home" returns to Town. The whole route closes cleanly: stop any time, feel
 * finished.
 *
 * Input is touch-first: an on-screen D-pad (and an interact button) drive movement,
 * with arrow/WASD keys as a desktop convenience — never keyboard-only. The biome art
 * is a soft programmatic placeholder (Graphics + a tinted ground), so the look is
 * swappable without touching traversal or the encounter pipeline. The renderer owns
 * no rules; truth (seed/date/society/encounters) comes through the GameSession + core.
 */
import Phaser from 'phaser';
import {
  generateRoute,
  emptyPity,
  scaleEncounter,
  followerScale,
  FOLLOWER_SCALE_DEFAULT,
  rollLegendaryOverlay,
} from '@/core';
import type { GameSession } from '@/session';
import type { GeneratedEncounter, SimTrainer, SpeciesData } from '@/shared';
import type { SpriteResolver } from './sprites/index.js';
import {
  makePlayerTrainer,
  trainerKeyForSimTrainer,
  type TrainerAvatar,
} from './sprites/trainerAvatar.js';
import { trainersManifestUrl } from './sprites/paths.js';
import {
  createOverworldActor,
  fitOverworldActor,
  type OverworldActor,
} from './sprites/overworldActor.js';
import {
  cardinalToDirection,
  initWander,
  pickLeadSpeciesId,
  stepFollow,
  stepWander,
  trailPointBehind,
  type Cardinal,
  type WanderBounds,
  type WanderState,
} from './overworld/wander.js';
import { COLORS, CSS, FONT, RADIUS, SPACING, isCoarsePointer } from './ui/theme.js';
import { Button } from './ui/Button.js';
import { DPad } from './ui/DPad.js';
import { DialogBox } from './ui/DialogBox.js';
import { Menu } from './ui/Menu.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { REGISTRY_SPRITES } from './PreloadScene.js';
import { getSession, peekSession } from './session.js';
import { biomeById, regionNodeById, REGION, type BiomeDef } from './world/biomes.js';
import {
  exitsFor,
  spawnAtEdge,
  areaDisplayName,
  OPPOSITE,
  TOWN_AREA,
  type AreaLink,
  type Edge,
} from './world/areaGraph.js';
import { computeSpriteScale, labelOffsetFor } from './routeSprites.js';
import { routeBannerTop } from './topBarLayout.js';

/** World extent (logical px) the camera scrolls across — generous but cozy. */
const WORLD_W = 1280;
const WORLD_H = 900;

/** Player walk speed (px/sec). Unhurried. */
const WALK_SPEED = 150;

/**
 * Movement clamp margins (px): the player's logical x is held to [CLAMP_X, WORLD_W-CLAMP_X]
 * and y to [CLAMP_Y, WORLD_H-CLAMP_Y]. The edge-seam trigger bands are measured off these
 * REACHABLE extremes (not the raw world rect) so every band is actually walkable-into.
 */
const CLAMP_X = 60;
const CLAMP_Y = 80;

/**
 * How deep (px) an edge-seam trigger band reaches inward from the reachable extreme. A
 * just-spawned player lands at spawnAtEdge's 90px inset — INSIDE this band — so the seams
 * stay disarmed until the player first steps clear of every band (see checkSeams).
 */
const SEAM_TRIGGER_DEPTH = 46;

/** The GB signpost arrow for each exit edge ("↑ Whispering Forest"). */
const SEAM_ARROW: Record<Edge, string> = { up: '↑', down: '↓', left: '←', right: '→' };

/**
 * Lead-Pokemon follower tuning (mirrors Town's spawnFollower: a ~one-tile gap and a
 * speed that keeps it tucked right behind the player). FOLLOW_SPEED sits just ABOVE
 * WALK_SPEED so the follower can always close any lag a long walk opened up and never
 * drifts screen-distant. The trailing arc-length (FOLLOW_GAP_BASE, below) is scaled
 * down with the sprites on a compact phone via `this.followGap`.
 */
const FOLLOW_SPEED = 170;

/**
 * DESKTOP base trainer footprint sides (px) on the route — a follower is sized as
 * `trainerSide * followerScale(height)`, hard-capped at 1.2× the trainer
 * (CLAUDE.md: followers sized by Pokedex height). PLAYER/NPC trainers fit to
 * these sides (see refitTrainer calls); the follower's tallest side is then a
 * height-driven fraction of them.
 *
 * These are the ROOMY desktop values; on a narrow/portrait phone every footprint is
 * multiplied by `spriteScale` (≈COMPACT_SPRITE_SCALE) so the route reads cleanly in a
 * ~390px viewport — see `computeSpriteScale` + `responsiveSide`. The height-based
 * sizing (followerScale / heightScaledSide, 1.2× cap) stays relative to the already-
 * scaled base, so a follower is still ≤1.2× its (now smaller) leader.
 */
const PLAYER_TRAINER_SIDE = 100;
const NPC_TRAINER_SIDE = 96;

/**
 * DESKTOP base footprint side (px) for a VISIBLE WILD creature milling on the route —
 * the neutral "average-height" size (scaled by `spriteScale` on a compact phone). The
 * actual rendered side is this scaled by the species' Pokedex-height ratio
 * (followerScale), so a tiny wild (caterpie) reads small and a giant (onix/gyarados)
 * reads big — the same height→scale model the leader's follower uses (CLAUDE.md: sized
 * by Pokedex height). Capped via that ratio's own 1.2× ceiling, and fitted off the
 * STABLE PMD frame (not the live walk frame) so a walking wild keeps a constant
 * footprint instead of ballooning.
 */
const WILD_CREATURE_SIDE = 84;

/**
 * Footprint side (px) scaled by a species' Pokedex height: `baseSide *
 * followerScale(height)`. Used both for a trainer's trailing follower (base = the
 * trainer's side, so the result is leader-relative + hard-capped at 1.2×) and for a
 * visible wild creature on the route (base = WILD_CREATURE_SIDE). A missing/invalid
 * height falls back to the neutral default ratio.
 */
function heightScaledSide(baseSide: number, heightMeters: number | undefined): number {
  const ratio =
    typeof heightMeters === 'number' && heightMeters > 0
      ? followerScale(heightMeters)
      : FOLLOWER_SCALE_DEFAULT;
  return baseSide * ratio;
}

/** Distance (px) at which an interactable becomes "approached". */
const APPROACH_RADIUS = 56;

/** The greeting dialog box's height (px) — the bottom strip it reserves. */
const DIALOG_H = 116;

/**
 * DESKTOP floating-label resting offsets (px above a sprite). Trainer NAME labels ride
 * higher than wild SPECIES labels so the two tag rows never coincide; if a wandering
 * trainer still drifts onto a wild creature, resolveLabelOverlaps() dims one.
 *
 * These are the desktop-footprint offsets; on a compact phone the labels sit closer to
 * the (smaller) sprites — see `labelOffsetFor`, which re-derives the lift from the
 * RESPONSIVE footprint side rather than assuming a fixed desktop sprite height.
 */
const TRAINER_LABEL_OFFSET_Y = -104;
const WILD_LABEL_OFFSET_Y = -78;

/**
 * Warm phrasings of each biome KIND for the roaming-legendary rumor line. Keyed by the
 * `BiomeKind` the legendary is drifting toward (core/legendary.roamingBiomeKind); any
 * unmapped kind falls back to a calm generic in surfaceRoamingRumor.
 */
const ROAMING_BIOME_PHRASE: Readonly<Record<string, string>> = {
  meadow: 'the sunlit meadows',
  forest: 'the deep forest',
  cave: 'the quiet caverns',
  mountain: 'the high mountain crags',
  lake: 'the still lake',
  sea: 'the open sea',
  beach: 'the windswept shore',
  marsh: 'the misted marsh',
  grassland: 'the open grasslands',
  ruins: 'the old ruins',
};

/**
 * Small arc-length (px) the player's lead Pokemon trails behind, at the DESKTOP scale.
 * On a compact phone the gap shrinks with the sprites (`responsiveFollowGap`) so the
 * (smaller) follower still tucks ~one tile behind rather than a screen away.
 */
const FOLLOW_GAP_BASE = 30;
/** The same trailing gap for an NPC trainer's lead-Pokemon follower (desktop scale). */
const NPC_FOLLOW_GAP_BASE = 30;

/** Below this center-distance (px) two floating labels are treated as colliding. */
const LABEL_COLLIDE_Y = 16;
const LABEL_COLLIDE_X = 72;

/** A placed, approachable thing in the overworld (wild mon or sim trainer). */
interface WorldActor {
  readonly kind: 'wild' | 'trainer';
  /** Current world position (trainers wander, so this is mutable). */
  x: number;
  y: number;
  /** The placeholder shown until the sprite resolves (then hidden). */
  readonly placeholder: Phaser.GameObjects.Graphics;
  /** A WILD creature's directional PMD chibi (set once resolved; never null). */
  actor?: OverworldActor;
  /** A sim trainer's real TRAINER sprite (set immediately; placeholder swaps in). */
  avatar?: TrainerAvatar;
  readonly label: Phaser.GameObjects.Text;
  /**
   * The label's resting offset above the sprite (so anti-overlap can restore it).
   * Re-derived from the responsive footprint on a resize that crosses the breakpoint.
   */
  labelOffsetY: number;
  /** The label's resting alpha (so anti-overlap can dim then restore it). */
  readonly labelBaseAlpha: number;
  /**
   * The actor's species Pokedex height (m), kept so a resize can re-derive the
   * height-scaled footprint + label offset against the new responsive base.
   */
  readonly heightMeters?: number;
  /** Set for wild encounters. */
  readonly encounter?: GeneratedEncounter;
  /**
   * True for the rare ROAMING legendary (core/legendary overlay). It surfaces at its
   * own HIGH level (BST-scaled, always > 50) and must BYPASS the Cozy↔Spicy level
   * cap, which would otherwise drag it down to the player's lead.
   */
  readonly legendary?: boolean;
  /** Set for sim trainers. */
  readonly trainer?: SimTrainer;
  /** True once the player has interacted (so we don't re-trigger). */
  spent: boolean;
  /** Sim-trainer deliberate cardinal stop-and-go wander state. */
  wander?: WanderState;
  /** True while moving (so we settle the bob on arrival). */
  moving?: boolean;
  /** A sim trainer's lead Pokemon (PMD chibi) trailing them, like the player's. */
  follower?: OverworldActor;
  /** The NPC follower's species height (m), for re-fitting on a responsive resize. */
  followerHeight?: number;
  /** Breadcrumb trail of the trainer's path the follower walks behind. */
  followerTrail?: { x: number; y: number }[];
}

/**
 * A walkable edge transition: a link to a neighbour area (from the region graph) plus the
 * signpost display objects marking it. The trigger BAND is derived on the fly from the
 * link's edge (see inBand), so only the display objects need tracking for teardown.
 */
interface Seam {
  readonly link: AreaLink;
  readonly objects: Phaser.GameObjects.GameObject[];
}

export class RouteScene extends Phaser.Scene {
  private layout!: Layout;
  private session!: GameSession;
  private resolver?: SpriteResolver;
  private reduced = false;

  private nodeId: string = REGION.startNodeId;
  private biome!: BiomeDef;

  /**
   * Responsive overworld sprite-footprint multiplier (1 on desktop, ~0.55 on a
   * narrow/portrait phone). Re-derived from the live layout each relayout; every
   * trainer/follower/wild footprint side is multiplied by it so the route reads cleanly
   * on a small viewport. The height-based sizing stays relative to this scaled base.
   */
  private spriteScale = 1;

  // World layer (scrolled by the camera) vs UI layer (screen-fixed).
  private worldLayer!: Phaser.GameObjects.Container;
  private ground?: Phaser.GameObjects.Graphics;
  /** The player's real TRAINER avatar; its `.sprite` is what the camera follows. */
  private playerAvatar!: TrainerAvatar;
  private get player(): Phaser.GameObjects.Sprite {
    return this.playerAvatar.sprite;
  }
  private playerMoving = false;
  /** Logical player position (the bob offset is added on top for the sprite's y). */
  private playerY = 0;
  /** Walk-bob phase (radians) advanced while the player is moving. */
  private playerBobPhase = 0;
  /** Available trainer keys (manifest) for deterministic NPC sprite assignment. */
  private trainerKeys: readonly string[] = [];
  /** The lead-Pokemon follower (PMD chibi); a step behind the player. */
  private follower?: OverworldActor;
  /** The player follower's species height (m), for re-fitting on a responsive resize. */
  private followerHeight?: number;
  private followerTrail: { x: number; y: number }[] = [];
  private actors: WorldActor[] = [];

  // On-screen controls (screen-fixed).
  private dpad?: DPad;
  private interactButton?: Button;
  private homeButton?: Button;
  private banner?: Phaser.GameObjects.Text;
  private dialog?: DialogBox;
  /** The classic ▶-cursor choice menu shown during a trainer greeting. */
  private greetMenu?: Menu;
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd?: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;

  private busy = false;
  private nearActor: WorldActor | null = null;

  /**
   * Edge seams to neighbouring areas (built from exitsFor(nodeId)). Walking into a seam's
   * band travels to that neighbour; on a phone the signpost is also a big tap target.
   */
  private seams: Seam[] = [];
  /**
   * Seam debounce: a player who just spawned INSIDE an entry band must not immediately
   * re-trigger the exit. Seams stay disarmed until the player first steps clear of every
   * band; only then does walking back into one travel.
   */
  private seamsArmed = false;
  /** True from the instant a seam transition starts, so it can never double-fire. */
  private traveling = false;

  /** The exit edge carried in by a seam transition (spawn rule (a)); cleared each init. */
  private entryEdge?: Edge;
  /** The player's last cardinal facing, persisted with the overworld position. */
  private playerFacing: Edge = 'up';
  /** Throttle clock (this.time.now) for the periodic move-time position save. */
  private lastPosSaveAt = 0;

  /**
   * The day's roaming-legendary rumor (the legendary currently drifting + the biome
   * KIND it is heading toward), set by buildLegendary. Surfaced as a quiet lore line
   * on arrival, hinting where to chase it — null when the dataset has no legendaries.
   */
  private roamingRumor: { name: string; kind: string } | null = null;

  constructor() {
    super('Route');
  }

  init(data: { nodeId?: string; biomeId?: string; entryEdge?: Edge }): void {
    if (data?.nodeId) this.nodeId = data.nodeId;
    // A seam transition carries the edge we arrive through (spawn rule (a)). Re-read it
    // every entry — a battle-return (nodeId only, no entryEdge) must clear a stale value
    // so the pre-battle position (spawn rule (b)) is restored, not an edge spawn.
    this.entryEdge = data?.entryEdge;
  }

  create(): void {
    // Phaser REUSES this scene instance, so create() re-runs on every RE-ENTRY (Route →
    // Battle → back, Route → Town → Route). Instance fields (the actors/d-pad arrays,
    // the follower trail, busy/nearActor) survive between visits — reset them here so a
    // re-entry never stacks doubled, dead controls atop the new ones.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    // Derive the responsive sprite-footprint multiplier BEFORE building the player /
    // follower / encounters, so they are sized for this viewport from the first frame
    // (smaller on a narrow phone) rather than spawning desktop-huge then snapping.
    this.refreshSpriteScale();
    this.reduced = isReducedMotion();
    this.resolver = this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined;

    // The session is the single source of truth; if it's somehow missing (a wiring
    // bug), fail loudly rather than render an empty world — but never mid-session.
    this.session = getSession(this);

    const node = regionNodeById(this.nodeId) ?? regionNodeById(REGION.startNodeId);
    this.biome = (node && biomeById(node.biomeId)) ?? biomeById('meadow')!;
    if (node) this.nodeId = node.id;

    this.worldLayer = this.add.container(0, 0);
    this.ground = this.add.graphics();
    this.worldLayer.add(this.ground);

    this.buildPlayer();
    this.buildControls();
    this.buildBanner();
    // Edge seams to the neighbouring areas (the connected walkable overworld). Built
    // before the first relayout so drawGround can paint each seam's soft ground path.
    this.buildSeams();

    // Fetch the trainer manifest FIRST so the (single) wandering sim trainer gets a
    // deterministic, varied sprite rather than always the default. Non-throwing; an
    // empty manifest just yields the default trainer key. The wild creatures + the
    // trainer are placed once the keys land (a frame later; the world is calm by then).
    void this.loadTrainerKeys().then(() => {
      if (!this.scene.isActive()) return;
      this.buildEncounters();
      // A quiet rumor of the roaming legendary, after the biome-name banner settles —
      // ambient wonder hinting where to chase it, never a quest (CLAUDE.md lore rules).
      this.surfaceRoamingRumor();
    });

    this.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H);
    this.cameras.main.startFollow(this.player, true, 0.12, 0.12);

    this.setupKeyboard();

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);

    this.showBanner(`${this.biome.name}`);
  }

  /**
   * Drop every reference a prior visit's create() populated, so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears
   * the arrays/flags so nothing stacks. Safe on a first entry (all already empty).
   */
  private resetState(): void {
    this.actors = [];
    this.dpad = undefined;
    this.follower = undefined;
    this.followerHeight = undefined;
    this.followerTrail = [];
    this.interactButton = undefined;
    this.homeButton = undefined;
    this.banner = undefined;
    this.dialog = undefined;
    this.greetMenu = undefined;
    this.roamingRumor = null;
    this.ground = undefined;
    this.cursors = undefined;
    this.wasd = undefined;
    this.playerMoving = false;
    this.playerBobPhase = 0;
    this.busy = false;
    this.nearActor = null;
    // Seam state (entryEdge is owned by init(), which runs just before this — leave it).
    this.seams = [];
    this.seamsArmed = false;
    this.traveling = false;
    this.playerFacing = 'up';
    this.lastPosSaveAt = 0;
  }

  // ── responsive sprite sizing ──────────────────────────────────────────────────

  /** Re-derive `spriteScale` from the live layout (1 desktop, ~0.55 compact phone). */
  private refreshSpriteScale(): void {
    this.spriteScale = computeSpriteScale({
      isCompact: this.layout.isCompact,
      insetTop: this.layout.insets.top,
      insetBottom: this.layout.insets.bottom,
    });
  }

  /** Responsive player-trainer footprint side (px) for the current viewport. */
  private get playerSide(): number {
    return PLAYER_TRAINER_SIDE * this.spriteScale;
  }
  /** Responsive NPC-trainer footprint side (px). */
  private get npcSide(): number {
    return NPC_TRAINER_SIDE * this.spriteScale;
  }
  /** Responsive base wild-creature footprint side (px), before the per-species ratio. */
  private get wildSide(): number {
    return WILD_CREATURE_SIDE * this.spriteScale;
  }
  /** Responsive trailing gap (px) for the player's lead-Pokemon follower. */
  private get followGap(): number {
    return FOLLOW_GAP_BASE * this.spriteScale;
  }
  /** Responsive trailing gap (px) for an NPC trainer's lead-Pokemon follower. */
  private get npcFollowGap(): number {
    return NPC_FOLLOW_GAP_BASE * this.spriteScale;
  }

  // ── world building ─────────────────────────────────────────────────────────

  private buildPlayer(): void {
    const spawn = this.resolveSpawn();
    const startX = spawn.x;
    const startY = spawn.y;
    this.playerFacing = spawn.facing;

    // The player is a real Pokemon-game TRAINER (a vendored Showdown sprite, chosen at
    // new-game) — distinct from the Pokemon chibis that walk behind / mill about. The
    // avatar returns synchronously (placeholder, then the real PNG swaps in).
    this.playerAvatar = makePlayerTrainer(this, this.session.avatar.trainerKey, startX, startY);
    // Overworld sprites render ~2× larger so everyone reads clearly in the main view —
    // shrunk to the responsive footprint on a narrow/portrait phone.
    this.refitTrainer(this.playerAvatar, this.playerSide);
    // Face the arrival direction (the static trainer front only carries a L/R flip cue).
    if (spawn.facing === 'left') this.player.setFlipX(true);
    this.player.setDepth(10);
    this.worldLayer.add(this.player);
    this.playerY = startY;
    // Seed the trail at the player's start so the follower has a point to trail from the
    // very first frame (an empty trail would strand it at its spawn until crumbs accrue).
    this.followerTrail = [{ x: startX, y: startY }];

    // The lead Pokemon follows as a correctly-SLICED, directional PMD chibi. Spawn it
    // right next to the player (a ~one-tile gap, like Town) so it never starts distant.
    void this.loadFollower(startX - this.followGap, startY);
  }

  /**
   * Where the player spawns this entry, in priority order (notes/r3-design/overworld.md):
   *   (a) an `entryEdge` from a seam transition → just inside that edge (spawnAtEdge);
   *   (b) a saved overworld position for THIS area (tab-close / battle-return) → restore,
   *       clamped in-bounds so a stale/edited save never strands the player off-screen;
   *   (c) today's default — bottom-centre, facing up the route.
   */
  private resolveSpawn(): { x: number; y: number; facing: Edge } {
    if (this.entryEdge) {
      return spawnAtEdge(this.entryEdge, WORLD_W, WORLD_H);
    }
    const ow = this.session.overworld;
    if (ow && ow.area === this.nodeId) {
      return {
        x: Phaser.Math.Clamp(ow.x, CLAMP_X, WORLD_W - CLAMP_X),
        y: Phaser.Math.Clamp(ow.y, CLAMP_Y, WORLD_H - CLAMP_Y),
        facing: ow.facing,
      };
    }
    return { x: WORLD_W / 2, y: WORLD_H - 140, facing: 'up' };
  }

  /**
   * Fit a trainer avatar to `maxSide`, re-fitting a few times to catch the async swap
   * from the small placeholder to the (larger) real Showdown PNG — so a trainer never
   * ends up oversized once the art lands.
   */
  private refitTrainer(avatar: TrainerAvatar, maxSide: number): void {
    const fit = (): void => {
      if (!avatar.sprite.active) return;
      fitSprite(avatar.sprite, maxSide);
    };
    fit();
    this.time.delayedCall(120, fit);
    this.time.delayedCall(400, fit);
    this.time.delayedCall(1000, fit);
  }

  /** Fetch the trainer-sprite manifest (the available keys); never throws. */
  private async loadTrainerKeys(): Promise<void> {
    try {
      const res = await fetch(trainersManifestUrl());
      if (!res.ok) return;
      const manifest = (await res.json()) as { keys?: string[] };
      if (!this.scene.isActive()) return;
      if (Array.isArray(manifest.keys)) this.trainerKeys = manifest.keys;
    } catch {
      // No manifest (offline / thin build): trainers fall back to the default sprite.
    }
  }

  private async loadFollower(x: number, y: number): Promise<void> {
    if (!this.resolver) return;
    const follower = this.session.follower;
    if (!follower) return;
    const sp = this.session.bundle.index.speciesById(follower.speciesId);
    if (!sp) return;
    const ow = await this.resolver.overworldSprite(sp.dexNo);
    if (!this.scene.isActive()) return;
    const actor = createOverworldActor(this, ow, x, y);
    // Fit by the STABLE nominal frame size (constant across idle + walk), so the
    // follower's footprint stays put through the walk cycle instead of ballooning.
    // Sized off the RESPONSIVE player side, so the height ratio + 1.2× cap stay relative
    // to the (smaller-on-a-phone) leader.
    fitOverworldActor(actor, heightScaledSide(this.playerSide, sp.height), 4);
    actor.sprite.setDepth(9);
    actor.face('north'); // trailing the player, who starts facing up the route
    this.worldLayer.add(actor.sprite);
    this.follower = actor;
    this.followerHeight = sp.height;
    // Defensive: if buildPlayer's seed was somehow lost, re-seed at the player's spot so
    // the follower has a trail point to chase from frame one (never stranded at spawn).
    if (this.followerTrail.length === 0) {
      this.followerTrail.push({ x: this.player.x, y: this.playerY });
    }
  }

  /**
   * Roll today's route via core/encounter (deterministic from seed+date+node) and
   * scatter the encounters + an occasional sim trainer across the walkable area.
   */
  private buildEncounters(): void {
    const node = regionNodeById(this.nodeId);
    if (!node) return;

    let encounters: readonly GeneratedEncounter[];
    try {
      const route = generateRoute({
        worldSeed: this.session.seed,
        inGameDate: this.session.currentDate,
        node,
        biome: this.biome,
        pity: emptyPity(),
      });
      encounters = route.encounters;
    } catch {
      // generateRoute is pure + total over valid biome tables; stay defensive so a
      // tired player never meets a crash instead of a calm walk.
      encounters = [];
    }

    // Deterministic placement: a fork of the expedition RNG so the same day lays the
    // same world out, anchoring the once-a-day cadence (and re-entrancy is harmless).
    const rng = this.session.expeditionRng(this.nodeId);

    const padX = 120;
    const padTop = 120;
    const padBottom = 260; // leave the player's start area + on-screen pad clear

    for (const enc of encounters) {
      const sp = this.session.bundle.index.speciesById(enc.speciesId);
      if (!sp) continue; // dataset may lack a slug (thin fixtures build) — skip calmly
      const x = padX + rng.float() * (WORLD_W - padX * 2);
      const y = padTop + rng.float() * (WORLD_H - padTop - padBottom);
      this.placeWild(enc, sp, x, y);
    }

    // Occasionally a friendly simulated trainer is wandering here too (homeNodeId
    // matches, or a regular passing through). ~60% of expeditions meet someone, and a
    // roomy desktop may show a second — a LOD cap thins the LIVE crowd on a phone
    // (isCoarsePointer → 1) while desktop stays livelier (2). Purely a rendering cap;
    // the society tick is untouched, and the wilds above stay exactly as generated.
    // These rolls draw from `rng` AFTER the wilds + BEFORE the separately-forked
    // legendary/decor RNGs, so trainer LOD never shifts wild or legendary placement.
    const walkerCap = isCoarsePointer() ? 1 : 2;
    const placedIds = new Set<string>();
    for (let i = 0; i < walkerCap; i++) {
      if (rng.float() >= (i === 0 ? 0.6 : 0.35)) continue;
      const trainer = this.pickTrainerForNode(rng);
      if (!trainer || placedIds.has(trainer.id)) continue;
      placedIds.add(trainer.id);
      const x = padX + rng.float() * (WORLD_W - padX * 2);
      const y = padTop + rng.float() * (WORLD_H - padTop - padBottom);
      this.placeTrainer(trainer, x, y);
    }

    // ── The rare, high-level ROAMING legendary (core/legendary overlay) ──────────
    // A single legendary drifts the region; on a QUALIFYING biome it may surface at
    // its own BST-scaled HIGH level (always > 50), atop the normal route. Rare per
    // outing, but pity-guaranteed to come around. Deterministic from seed+date+node.
    this.buildLegendary(padX, padTop, padBottom);
  }

  /**
   * Roll + place the roaming legendary overlay (if it surfaces here today) and stash
   * the roaming-target rumor for the day's lore line. Pure/deterministic via
   * core/legendary — adds nothing to the frozen Save; never touches the biome tables.
   */
  private buildLegendary(padX: number, padTop: number, padBottom: number): void {
    const node = regionNodeById(this.nodeId);
    if (!node) return;

    let roll: ReturnType<typeof rollLegendaryOverlay>;
    try {
      roll = rollLegendaryOverlay({
        worldSeed: this.session.seed,
        inGameDate: this.session.currentDate,
        nodeId: this.nodeId,
        biome: this.biome,
        species: this.session.bundle.species,
      });
    } catch {
      return; // stay defensive — a missing/odd dataset never crashes the walk
    }

    // Remember the roaming target + its drift biome so the route can offer a quiet
    // rumor ("something legendary is drifting toward the ...") even when none spawns.
    this.roamingRumor =
      roll.roaming && roll.roamingKind ? { name: roll.roaming.name, kind: roll.roamingKind } : null;

    if (!roll.encounter) return;

    const sp = this.session.bundle.index.speciesById(roll.encounter.speciesId);
    if (!sp) return; // thin dataset lacks this legendary — skip calmly, rumor stands

    // Place it apart from the start area, like any visible wild — approachable, at its
    // high level. A dedicated RNG fork keeps placement deterministic + re-entry-safe.
    const placeRng = this.session.expeditionRng(`${this.nodeId}|legendary`);
    const x = padX + placeRng.float() * (WORLD_W - padX * 2);
    const y = padTop + placeRng.float() * (WORLD_H - padTop - padBottom);
    this.placeWild(roll.encounter, sp, x, y, true);
  }

  /** A trainer who calls this node home, else any regular, for a warm cameo. */
  private pickTrainerForNode(rng: ReturnType<GameSession['expeditionRng']>): SimTrainer | null {
    const here = this.session.society.filter((t) => t.homeNodeId === this.nodeId);
    const pool = here.length > 0 ? here : this.session.regulars();
    if (pool.length === 0) return null;
    return pool[rng.int(pool.length)] ?? null;
  }

  private placeWild(
    enc: GeneratedEncounter,
    sp: SpeciesData,
    x: number,
    y: number,
    legendary = false,
  ): void {
    // A roaming legendary glows in the warm accent (a hush of something special),
    // a shiny in warm, an ordinary wild in the cool accent.
    const placeholder = this.makeActorPlaceholder(
      x,
      y,
      legendary || enc.shiny ? COLORS.warm : COLORS.accent,
    );
    const labelText = legendary ? `${sp.name} ✦✦` : enc.shiny ? `${sp.name} ✦` : sp.name;
    // The visible wild's RESPONSIVE rendered footprint (base side × the per-species
    // height ratio), used both to fit the sprite and to seat its label just above its
    // head — so on a phone the tag hugs the smaller sprite instead of floating high.
    const renderedSide = heightScaledSide(this.wildSide, sp.height);
    // Wild species labels ride LOWER than trainer name labels (a smaller lift off the
    // footprint), so even when a wandering trainer crosses a wild creature the two tags
    // don't sit on the same line; the per-frame anti-overlap pass dims whichever grazes.
    const labelOffsetY = labelOffsetFor(WILD_LABEL_OFFSET_Y, WILD_CREATURE_SIDE, renderedSide);
    const labelBaseAlpha = 0.85;
    const label = this.add
      .text(x, y + labelOffsetY, labelText, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: legendary || enc.shiny ? CSS.warm : CSS.textMuted,
      })
      .setOrigin(0.5)
      .setDepth(6)
      .setAlpha(labelBaseAlpha);
    this.worldLayer.add(label);

    const actor: WorldActor = {
      kind: 'wild',
      x,
      y,
      placeholder,
      label,
      labelOffsetY,
      labelBaseAlpha,
      heightMeters: sp.height,
      encounter: enc,
      legendary,
      spent: false,
    };
    this.actors.push(actor);
    // Size the visible wild by its species' Pokedex height (off the RESPONSIVE base
    // creature footprint), so a tiny caterpie reads small and a big onix reads big —
    // the same height→scale model the leader's follower uses. fitOverworldActor sizes
    // off the STABLE frame, so a walking wild keeps a constant footprint (no balloon).
    void this.loadActorSprite(actor, sp.dexNo, renderedSide);
  }

  private placeTrainer(trainer: SimTrainer, x: number, y: number): void {
    const placeholder = this.makeActorPlaceholder(x, y, COLORS.accent);
    // Trainer name labels float HIGHER than wild species labels (a larger lift off the
    // footprint) so the two never share a line; depth 7 also keeps the name on top if
    // they still touch. Derived from the RESPONSIVE trainer side so it hugs the sprite
    // on a phone instead of floating at the fixed desktop offset.
    const labelOffsetY = labelOffsetFor(TRAINER_LABEL_OFFSET_Y, NPC_TRAINER_SIDE, this.npcSide);
    const label = this.add
      .text(x, y + labelOffsetY, trainer.username, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.accent,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(7);
    this.worldLayer.add(label);

    // A wandering sim trainer is a real TRAINER sprite (same Foundation avatar as the
    // player), keyed deterministically off the trainer id so they always wear the same
    // look. Deliberate cardinal stop-and-go wander: walk a tile or two, pause, repeat.
    const key = trainerKeyForSimTrainer(trainer.id, this.trainerKeys);
    const avatar = makePlayerTrainer(this, key, x, y);
    this.refitTrainer(avatar, this.npcSide);
    avatar.sprite.setDepth(6);
    this.worldLayer.add(avatar.sprite);

    const actor: WorldActor = {
      kind: 'trainer',
      x,
      y,
      placeholder,
      label,
      labelOffsetY,
      labelBaseAlpha: 1,
      trainer,
      spent: false,
      avatar,
      wander: initWander(x, y),
      moving: false,
      followerTrail: [{ x, y }],
    };
    this.actors.push(actor);
    placeholder.setVisible(false);

    // The trainer's lead Pokemon trails them as a PMD chibi (deterministic pick) —
    // every NPC has a partner, just like the player. Pooled by dex via the resolver.
    void this.loadTrainerFollower(actor, trainer);
  }

  /** Resolve + attach an NPC trainer's trailing lead-Pokemon chibi (never throws). */
  private async loadTrainerFollower(actor: WorldActor, trainer: SimTrainer): Promise<void> {
    if (!this.resolver) return;
    const speciesId = pickLeadSpeciesId(trainer);
    if (!speciesId) return;
    const sp = this.session.bundle.index.speciesById(speciesId);
    if (!sp) return;
    const ow = await this.resolver.overworldSprite(sp.dexNo);
    if (!this.scene.isActive()) return;
    const follower = createOverworldActor(this, ow, actor.x - this.npcFollowGap, actor.y);
    // Stable-frame fit (see loadFollower): footprint constant across the walk cycle,
    // and sized + capped against THIS NPC's RESPONSIVE footprint (not the player's).
    fitOverworldActor(follower, heightScaledSide(this.npcSide, sp.height), 4);
    follower.sprite.setDepth(5);
    follower.face('south');
    this.worldLayer.add(follower.sprite);
    actor.follower = follower;
    actor.followerHeight = sp.height;
  }

  /** A soft rounded placeholder shown until an actor's PMD chibi resolves. */
  private makeActorPlaceholder(x: number, y: number, tint: number): Phaser.GameObjects.Graphics {
    const g = this.add.graphics().setDepth(5);
    g.fillStyle(tint, 0.85);
    g.fillRoundedRect(-12, -28, 24, 28, RADIUS.sm);
    g.setPosition(x, y);
    this.worldLayer.add(g);
    return g;
  }

  /**
   * Resolve an actor's correctly-SLICED, directional PMD chibi via the Foundation
   * overworld method (pools by dex; fallback chain inside; never throws). Swaps it in
   * for the placeholder, fitted to `maxSide` (the caller passes a height-scaled side so
   * a wild reads small/large by species), facing south (toward the player coming up the
   * route).
   */
  private async loadActorSprite(actor: WorldActor, dexNo: number, maxSide: number): Promise<void> {
    if (!this.resolver) return;
    const ow = await this.resolver.overworldSprite(dexNo);
    if (!this.scene.isActive()) return;
    const built = createOverworldActor(this, ow, actor.x, actor.y);
    // Stable-frame fit so a walking/idling wild chibi keeps a constant footprint.
    fitOverworldActor(built, maxSide, 4);
    built.sprite.setDepth(5);
    built.face('south');
    this.worldLayer.add(built.sprite);
    actor.actor = built;
    actor.placeholder.setVisible(false);
  }

  // ── edge seams (the connected walkable overworld) ─────────────────────────────

  /**
   * Build a signpost + trigger band for every walkable exit of this area (exitsFor from
   * the region graph). World-space so the signs scroll with the terrain; on a phone each
   * signpost is a big tap target that travels the same seam.
   */
  private buildSeams(): void {
    for (const link of exitsFor(this.nodeId)) {
      const objects = this.makeSignpost(link);
      this.seams.push({ link, objects });
    }
  }

  /** Anchor point (world-space) for a seam's signpost — just inside its edge band. */
  private signpostAnchor(edge: Edge): { x: number; y: number } {
    const inset = 168;
    switch (edge) {
      case 'up':
        return { x: WORLD_W / 2, y: inset };
      case 'down':
        return { x: WORLD_W / 2, y: WORLD_H - inset };
      case 'left':
        return { x: inset, y: WORLD_H / 2 };
      case 'right':
        return { x: WORLD_W - inset, y: WORLD_H / 2 };
    }
  }

  /**
   * A cream GB signpost ("↑ Whispering Forest") in the classic message-box chrome
   * (cream fill + dark double border, matching DialogBox), with a little post beneath.
   * Tappable (phone travel target); returns the objects so teardown can drop them.
   */
  private makeSignpost(link: AreaLink): Phaser.GameObjects.GameObject[] {
    const { x, y } = this.signpostAnchor(link.edge);
    const label = `${SEAM_ARROW[link.edge]} ${areaDisplayName(link.toArea)}`;
    const text = this.add
      .text(x, y, label, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.boxText,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(9);
    const w = text.width + SPACING.md * 2;
    const h = text.height + SPACING.sm * 2;
    const r = RADIUS.sm;

    const g = this.add.graphics().setDepth(8);
    // Drop shadow, cream paper, then the iconic dark double border (as DialogBox.drawBox).
    g.fillStyle(COLORS.scrim, 0.25);
    g.fillRoundedRect(x - w / 2 + 2, y - h / 2 + 4, w, h, r);
    g.fillStyle(COLORS.boxFill, 1);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, r);
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, r);
    g.lineStyle(1, COLORS.boxBorderInner, 1);
    g.strokeRoundedRect(x - w / 2 + 4, y - h / 2 + 4, w - 8, h - 8, Math.max(2, r - 2));
    // A short post below the board so it reads as a planted signpost.
    g.fillStyle(COLORS.boxBorder, 1);
    g.fillRect(x - 3, y + h / 2, 6, 18);
    this.worldLayer.add(g);
    this.worldLayer.add(text);

    // Tap anywhere on the board to travel (a big touch target). The Graphics sits at
    // local (0,0) with the board drawn in world coords, so the world-space hit rect is
    // also its local rect — hit-testing through the follow-camera resolves correctly.
    const hit = new Phaser.Geom.Rectangle(x - w / 2, y - h / 2, w, h);
    g.setInteractive(hit, Phaser.Geom.Rectangle.Contains);
    g.on('pointerdown', () => this.travelThrough(link));

    return [g, text];
  }

  /** The seam whose trigger band currently contains the point, else null. */
  private seamAt(px: number, py: number): Seam | null {
    for (const s of this.seams) {
      if (this.inBand(s.link.edge, px, py)) return s;
    }
    return null;
  }

  /** Is the point inside the trigger band of the given edge? (measured off the clamp). */
  private inBand(edge: Edge, px: number, py: number): boolean {
    switch (edge) {
      case 'left':
        return px <= CLAMP_X + SEAM_TRIGGER_DEPTH;
      case 'right':
        return px >= WORLD_W - CLAMP_X - SEAM_TRIGGER_DEPTH;
      case 'up':
        return py <= CLAMP_Y + SEAM_TRIGGER_DEPTH;
      case 'down':
        return py >= WORLD_H - CLAMP_Y - SEAM_TRIGGER_DEPTH;
    }
  }

  /**
   * Per-frame seam check. DEBOUNCE: a player who just spawned inside an entry band must
   * not immediately bounce back out, so the seams stay disarmed until the player first
   * steps clear of every band; only then does walking into one travel. A tap on a
   * signpost bypasses this (an explicit request), guarded by travelThrough's busy flag.
   */
  private checkSeams(px: number, py: number): void {
    if (this.seams.length === 0 || this.traveling) return;
    const seam = this.seamAt(px, py);
    if (!this.seamsArmed) {
      if (!seam) this.seamsArmed = true;
      return;
    }
    if (seam) this.travelThrough(seam.link);
  }

  /**
   * Cross a seam: persist the arrival spot at the neighbour's OPPOSITE edge, then hand
   * off. The Town seam routes to TownScene with the arrival shape its scene expects; every
   * other seam re-enters RouteScene carrying the entry edge (spawn rule (a)).
   */
  private travelThrough(link: AreaLink): void {
    if (this.busy || this.traveling) return;
    this.traveling = true;
    this.busy = true;
    const spawn = spawnAtEdge(OPPOSITE[link.edge], WORLD_W, WORLD_H);
    if (link.toArea === TOWN_AREA) {
      this.session.saveOverworldPos({
        area: TOWN_AREA,
        x: spawn.x,
        y: spawn.y,
        facing: spawn.facing,
      });
      goTo(this, 'Town', { data: { fromRoute: true, entryEdge: 'down' } });
    } else {
      this.session.saveOverworldPos({
        area: link.toArea,
        x: spawn.x,
        y: spawn.y,
        facing: spawn.facing,
      });
      goTo(this, 'Route', { data: { nodeId: link.toArea, entryEdge: OPPOSITE[link.edge] } });
    }
  }

  /** Persist the player's current overworld position (throttled by the caller). */
  private persistPos(): void {
    this.lastPosSaveAt = this.time.now;
    this.session.saveOverworldPos({
      area: this.nodeId,
      x: this.player.x,
      y: this.playerY,
      facing: this.playerFacing,
    });
  }

  // ── on-screen controls ───────────────────────────────────────────────────────

  private buildControls(): void {
    // A 4-way D-pad: big, soft, touch-first tap-and-hold targets (the shared component).
    this.dpad = new DPad(this, { reducedMotion: this.reduced, depth: 100 });

    this.interactButton = new Button(this, 0, 0, {
      label: 'Say hi',
      variant: 'primary',
      width: 132,
      reducedMotion: this.reduced,
      onClick: () => this.interact(),
    });
    this.interactButton.setVisible(false).setScrollFactor(0).setDepth(100);

    this.homeButton = new Button(this, 0, 0, {
      label: 'Head home',
      variant: 'ghost',
      width: 148,
      reducedMotion: this.reduced,
      onClick: () => this.headHome(),
    });
    this.homeButton.setScrollFactor(0).setDepth(100);
  }

  private buildBanner(): void {
    this.banner = this.add
      .text(0, 0, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.md}px`,
        color: CSS.text,
        fontStyle: 'bold',
        backgroundColor: 'rgba(26,34,51,0.7)',
        padding: { x: SPACING.md, y: SPACING.xs },
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(100)
      .setAlpha(0);
  }

  private setupKeyboard(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    this.cursors = kb.createCursorKeys();
    this.wasd = {
      up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
    // Space/Enter interact with the nearest actor (keyboard convenience only).
    kb.on('keydown-SPACE', () => this.interact());
    kb.on('keydown-ENTER', () => this.interact());
  }

  // ── per-frame movement ───────────────────────────────────────────────────────

  override update(_time: number, delta: number): void {
    if (this.busy) return;
    const dt = Math.min(delta / 1000, 0.1);

    // Gather raw input (d-pad + keyboard), then collapse to a single CARDINAL direction:
    // the dominant axis wins, so the player walks up/down/left/right — never a floaty
    // diagonal glide. The on-screen d-pad already pushes one axis at a time.
    let rx = this.dpad?.vector.x ?? 0;
    let ry = this.dpad?.vector.y ?? 0;
    if (this.cursors?.left.isDown || this.wasd?.left.isDown) rx -= 1;
    if (this.cursors?.right.isDown || this.wasd?.right.isDown) rx += 1;
    if (this.cursors?.up.isDown || this.wasd?.up.isDown) ry -= 1;
    if (this.cursors?.down.isDown || this.wasd?.down.isDown) ry += 1;

    let dx = 0;
    let dy = 0;
    if (rx !== 0 || ry !== 0) {
      if (Math.abs(rx) >= Math.abs(ry) && rx !== 0) dx = Math.sign(rx);
      else dy = Math.sign(ry);
    }

    if (dx !== 0 || dy !== 0) {
      const nx = Phaser.Math.Clamp(
        this.player.x + dx * WALK_SPEED * dt,
        CLAMP_X,
        WORLD_W - CLAMP_X,
      );
      const ny = Phaser.Math.Clamp(this.playerY + dy * WALK_SPEED * dt, CLAMP_Y, WORLD_H - CLAMP_Y);
      this.playerMoving = true;
      this.player.x = nx;
      this.playerY = ny;
      // Remember the cardinal facing so it rides along in the persisted overworld position.
      this.playerFacing = dx > 0 ? 'right' : dx < 0 ? 'left' : dy > 0 ? 'down' : 'up';
      // A gentle walk-bob driven by hand: the avatar's own y-tween would FIGHT the
      // per-frame position writes (and pin vertical movement), so we add a small
      // bob offset on top of the logical y instead. Calm + reduced-motion aware.
      this.playerBobPhase += dt * 9;
      const bob = this.reduced ? 0 : Math.sin(this.playerBobPhase) * 1.6;
      this.player.y = this.playerY + bob;
      // Face the direction of travel (flip the static trainer front as a L/R cue).
      if (dx !== 0) this.player.setFlipX(dx < 0);
      // Persist at most ~1/s while walking so a tab-close mid-walk resumes in place.
      if (this.time.now - this.lastPosSaveAt > 1000) this.persistPos();
    } else if (this.playerMoving) {
      // Stopped: settle the player (drop the bob) + persist the resting spot immediately.
      this.playerMoving = false;
      this.playerBobPhase = 0;
      this.player.y = this.playerY;
      this.persistPos();
    }

    // Step the follower EVERY frame (exactly like Town's stepFollowerWalker), not only
    // while the player is moving. Recording a breadcrumb + chasing the trail-point even
    // when the player has stopped is what lets the follower CLOSE the gap after a walk;
    // gating it behind movement (the old bug) froze it screen-distant the instant the
    // player paused, so it "didn't follow".
    this.updateFollower(this.player.x, this.playerY, dt);

    this.stepTrainers(dt);
    this.updateNearest();
    this.resolveLabelOverlaps();
    // After movement resolves, test the edge seams (arm-once debounce inside).
    this.checkSeams(this.player.x, this.playerY);
  }

  /**
   * Light anti-overlap for the floating tags: a wandering trainer's NAME label and a
   * wild creature's SPECIES label already rest on different rows, but they can still
   * graze when a trainer drifts over a wild mon. Each frame, dim the lower-priority
   * label (the wild species tag) of any colliding pair so the two never read as one
   * smudged line — the trainer name (bold accent, depth 7) stays clear. Restores to
   * the resting alpha as soon as they part. Calm + cheap (O(n²) over a tiny actor set).
   */
  private resolveLabelOverlaps(): void {
    const live = this.actors.filter((a) => a.label.visible);
    for (const a of live) a.label.setAlpha(a.labelBaseAlpha);
    for (let i = 0; i < live.length; i++) {
      for (let j = i + 1; j < live.length; j++) {
        const a = live[i]!;
        const b = live[j]!;
        if (
          Math.abs(a.label.x - b.label.x) < LABEL_COLLIDE_X &&
          Math.abs(a.label.y - b.label.y) < LABEL_COLLIDE_Y
        ) {
          // Trainer name labels win; the wild species tag yields by fading back.
          const yielder = a.kind === 'wild' ? a : b.kind === 'wild' ? b : b;
          yielder.label.setAlpha(0.12);
        }
      }
    }
  }

  /** Wander bounds for the route's sim trainers (clamped inside the world rect). */
  private get trainerBounds(): WanderBounds {
    return { x: 0, y: 0, width: WORLD_W, height: WORLD_H, margin: 90 };
  }

  /**
   * Deliberate cardinal stop-and-go wander for the route's sim trainers: each walks a
   * tile or two in one cardinal direction, stops and idles a beat, sometimes turns, and
   * walks again — the classic Pokemon-overworld read, never a floaty diagonal drift. A
   * spent (already-greeted) trainer stands still. Their lead Pokemon trails them.
   */
  private stepTrainers(dt: number): void {
    for (const a of this.actors) {
      if (a.kind !== 'trainer' || !a.avatar || !a.wander) continue;
      const sprite = a.avatar.sprite;

      if (a.spent) {
        if (a.moving) {
          a.moving = false;
          sprite.setPosition(a.x, a.y);
        }
        // The follower settles too, still trailing where it stands.
        a.follower?.idle();
        continue;
      }

      const r = stepWander(a.wander, dt, this.trainerBounds);
      a.x = a.wander.x;
      a.y = a.wander.y;
      const dir: Cardinal = a.wander.facing;

      // Static trainer fronts: a small hand-driven bob carries the motion (the avatar's
      // own y-tween would FIGHT these per-frame position writes), plus a L/R flip cue.
      if (r.moving) {
        a.moving = true;
        if (dir === 'east' || dir === 'west') sprite.setFlipX(dir === 'west');
        const bob = this.reduced ? 0 : Math.sin(this.time.now / 110 + a.x) * 1.3;
        sprite.setPosition(a.x, a.y + bob);
      } else {
        a.moving = false;
        sprite.setPosition(a.x, a.y);
      }
      a.label.setPosition(a.x, a.y + a.labelOffsetY);

      this.stepTrainerFollower(a, dt);
    }
  }

  /** Trail an NPC trainer's lead Pokemon a fixed gap behind, walking the same path. */
  private stepTrainerFollower(a: WorldActor, dt: number): void {
    const f = a.follower;
    if (!f) return;
    const trail = a.followerTrail ?? (a.followerTrail = [{ x: a.x, y: a.y }]);
    const last = trail[trail.length - 1];
    if (!last || Math.abs(last.x - a.x) > 1 || Math.abs(last.y - a.y) > 1) {
      trail.push({ x: a.x, y: a.y });
      if (trail.length > 48) trail.shift();
    }
    const target = trailPointBehind(trail, this.npcFollowGap) ?? { x: a.x, y: a.y };
    const res = stepFollow({ x: f.sprite.x, y: f.sprite.y }, target, 80, dt, 1.5);
    if (res.moving && res.facing) {
      f.walk(cardinalToDirection(res.facing));
      f.sprite.setPosition(res.x, res.y);
    } else {
      f.idle();
    }
  }

  /**
   * The lead Pokémon trails a step behind the player along the SAME path — the calm,
   * deliberate companion read that works in Town. We record the player's breadcrumbs and
   * aim the follower a fixed arc-length behind them (trailPointBehind), then step toward
   * that point one cardinal axis at a time (stepFollow) — never a floaty diagonal glide,
   * never a teleport.
   *
   * Called EVERY frame (not only while moving), so the follower keeps closing the gap
   * after the player pauses — the exact behaviour Town's stepFollowerWalker has. Its
   * catch-up speed (FOLLOW_SPEED) sits a touch ABOVE the player's WALK_SPEED so it can
   * recover any lag a long walk introduced and settle ~one tile behind, rather than
   * drifting to the far side of the camera (the "doesn't follow / sits far away" bug).
   */
  private updateFollower(px: number, py: number, dt: number): void {
    const f = this.follower;
    if (!f) return;
    const trail = this.followerTrail;
    const last = trail[trail.length - 1];
    if (!last || Math.abs(last.x - px) > 1 || Math.abs(last.y - py) > 1) {
      trail.push({ x: px, y: py });
      if (trail.length > 48) trail.shift();
    }
    const target = trailPointBehind(trail, this.followGap) ?? { x: px, y: py };
    const res = stepFollow({ x: f.sprite.x, y: f.sprite.y }, target, FOLLOW_SPEED, dt, 1.5);
    if (res.moving && res.facing) {
      f.walk(cardinalToDirection(res.facing));
      f.sprite.setPosition(res.x, res.y);
    } else {
      f.idle();
    }
  }

  /** Find the nearest unspent actor within reach; surface the interact button. */
  private updateNearest(): void {
    let best: WorldActor | null = null;
    let bestD = APPROACH_RADIUS * APPROACH_RADIUS;
    for (const a of this.actors) {
      if (a.spent) continue;
      const dx = a.x - this.player.x;
      const dy = a.y - this.player.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    if (best !== this.nearActor) {
      this.nearActor = best;
      this.refreshInteractButton();
    }
  }

  private refreshInteractButton(): void {
    if (!this.interactButton) return;
    const a = this.nearActor;
    if (!a) {
      this.interactButton.setVisible(false);
      return;
    }
    if (a.kind === 'wild') {
      this.interactButton.setLabel('Approach').setVisible(true);
    } else {
      this.interactButton.setLabel('Say hi').setVisible(true);
    }
  }

  // ── interaction ────────────────────────────────────────────────────────────

  private interact(): void {
    if (this.busy) return;
    const a = this.nearActor;
    if (!a || a.spent) return;
    if (a.kind === 'wild' && a.encounter) {
      this.startWildBattle(a);
    } else if (a.kind === 'trainer' && a.trainer) {
      this.greetTrainer(a);
    }
  }

  /**
   * Start a wild battle, handing the pre-rolled encounter to the Battle scene. If the
   * player came from *behind* the wild mon (it was facing away as they approached),
   * grant a calm first-strike — a gentle reward for a sneak, never a penalty.
   */
  private startWildBattle(a: WorldActor): void {
    if (!a.encounter) return;

    // Apply the Cozy↔Spicy level dial + rubber-band + cap here, where the foe is
    // built (core/difficulty owns the maths). In Cozy the wild is dragged to AT or
    // BELOW the player's lead level, so a fresh starter never meets a higher foe —
    // the "lost to a wild Charmander" bug. Mild/Spicy keep their faithful bands.
    //
    // A ROAMING LEGENDARY is exempt: it must keep its own BST-scaled HIGH level
    // (always > 50), which the difficulty cap would otherwise drag down to the
    // player's lead. The catch stays hard via the (untouched) capture-rate formula,
    // not via the level — so it remains a memorable hush, never a routine win.
    const encounter = a.legendary ? a.encounter : this.difficultyScaledEncounter(a.encounter);

    // Build the wild opponent faithfully via the session (the engine owns the rules).
    const wild = this.session.wildInstance(encounter);
    if (!wild) {
      // Dataset lacks this species (thin fixtures) — bow out calmly, never crash.
      a.spent = true;
      this.nearActor = null;
      this.refreshInteractButton();
      return;
    }

    a.spent = true;
    this.busy = true;
    // Persist the pre-battle spot so the round-trip resumes here (spawn rule (b)), not
    // at the area default — the battle-return carries only nodeId, no entryEdge.
    this.persistPos();

    // "Sneaking behind": the player approached from the side the mon was looking away
    // from (its sprite faces -x by default; we approached from its right => behind).
    const sneak = this.player.x > a.x;
    const species = this.session.bundle.index.speciesById(a.encounter.speciesId);

    this.showBanner(sneak ? 'You crept up quietly…' : 'A wild encounter!');
    this.time.delayedCall(this.reduced ? 0 : 360, () => {
      if (!this.scene.isActive()) return;
      goTo(this, 'Battle', {
        data: {
          kind: 'wild',
          opponentParty: [wild],
          opponentName: a.encounter?.shiny ? `${species?.name ?? 'Wild'} ✦` : 'Wild',
          returnScene: 'Route',
          returnData: { nodeId: this.nodeId },
        },
      });
    });
  }

  /**
   * Re-level a pre-rolled encounter for the active Cozy↔Spicy dial. The biome table
   * picked the species + rarity + a base level; the difficulty layer (core/difficulty)
   * then scales that level, rubber-bands it toward the team, and caps it relative to
   * the player's lead — decisively in Cozy, where the foe ends up at/under your lead.
   * Species, slot and shiny are untouched (the encounter TABLE is owned elsewhere).
   */
  private difficultyScaledEncounter(enc: GeneratedEncounter): GeneratedEncounter {
    const lead = this.playerLeadLevel();
    const { enemyLevel } = scaleEncounter(this.session.currentSettings.difficulty, lead, enc.level);
    return enemyLevel === enc.level ? enc : { ...enc, level: enemyLevel };
  }

  /**
   * The player's lead level: the first non-fainted team member, else the strongest,
   * else a gentle floor of 5 (a fresh starter) so a brand-new save still gets the
   * Cozy cap. This anchors the rubber-band + cap to who actually leads the battle.
   */
  private playerLeadLevel(): number {
    const team = this.session.getTeam();
    if (team.length === 0) return 5;
    const healthy = team.find((m) => m.currentHp > 0);
    const lead = healthy ?? team.reduce((best, m) => (m.level > best.level ? m : best), team[0]!);
    return Math.max(1, lead.level);
  }

  /**
   * Greet a wandering sim trainer: a warm line, then calm choices — friendly (no
   * stakes) battle, propose a trade, or just chat. Everything is dip-in/ignore.
   */
  private greetTrainer(a: WorldActor): void {
    if (!a.trainer) return;
    const t = a.trainer;
    this.busy = true;

    // Tuck the movement controls away so the greeting dialog + choice row own the
    // bottom strip cleanly (no D-pad / interact button overlapping the conversation).
    this.setControlsVisible(false);

    const dialog = this.ensureDialog();
    dialog.show([`${t.username} waves you over with a warm smile.`, greetingLine(t)], t.username);
    dialog.setVisible(true);
    // Surface the calm choice menu alongside the greeting (dip-in / ignore).
    this.showTrainerChoices(a);
  }

  /** Show/hide the overworld movement controls (used during a greeting overlay). */
  private setControlsVisible(visible: boolean): void {
    this.dpad?.setVisible(visible);
    this.homeButton?.setVisible(visible);
    // The interact button only shows when near an unspent actor; let its own logic
    // re-surface it when controls return.
    if (!visible) this.interactButton?.setVisible(false);
    else this.refreshInteractButton();
  }

  private showTrainerChoices(a: WorldActor): void {
    if (!a.trainer) return;
    const t = a.trainer;

    const dismiss = (): void => {
      this.greetMenu?.destroy();
      this.greetMenu = undefined;
      this.dialog?.setVisible(false);
      a.spent = true;
      this.nearActor = null;
      this.busy = false;
      this.setControlsVisible(true);
    };

    // The classic ▶-cursor choice box — Friendly battle / Propose trade / Just chat.
    // It sits ABOVE the dialog box (which owns the bottom strip), screen-fixed so it
    // stays put as the world scrolls. Keyboard ↑/↓ + Enter, or tap a row; Esc closes.
    this.greetMenu = new Menu(this, 0, 0, {
      reducedMotion: this.reduced,
      width: Math.min(260, this.layout.safe.width - SPACING.lg * 2),
      items: [
        {
          label: 'Friendly battle',
          onSelect: () => {
            this.greetMenu?.destroy();
            this.greetMenu = undefined;
            this.startFriendlyBattle(a);
          },
        },
        {
          label: 'Propose trade',
          onSelect: () => {
            this.greetMenu?.destroy();
            this.greetMenu = undefined;
            this.openTrade(t);
          },
        },
        { label: 'Just chat', onSelect: dismiss },
      ],
      onCancel: dismiss,
    });
    this.greetMenu.setScrollFactor(0).setDepth(110);
    this.positionGreetMenu();
    this.greetMenu.focus();
  }

  /** Seat the greeting menu just above the dialog box (screen-fixed), centered. */
  private positionGreetMenu(): void {
    if (!this.greetMenu) return;
    const safe = this.layout.safe;
    const dialogTop = safe.y + safe.height - DIALOG_H - SPACING.md;
    this.greetMenu.setPosition(
      this.layout.centerX,
      dialogTop - this.greetMenu.boxHeight / 2 - SPACING.sm,
    );
  }

  /** A no-stakes friendly battle reuses the Battle scene with kind 'friendly'. */
  private startFriendlyBattle(a: WorldActor): void {
    if (!a.trainer) return;
    const party = this.session.friendlyParty(a.trainer.id);
    if (party.length === 0) {
      // The partner has no battle-ready team — keep it warm, just a chat instead.
      a.spent = true;
      this.dialog?.show(
        [`${a.trainer.username}: maybe another time — my team is resting!`],
        a.trainer.username,
      );
      this.busy = false;
      this.nearActor = null;
      this.setControlsVisible(true);
      return;
    }
    const trainerName = a.trainer.username;
    a.spent = true;
    this.busy = true;
    // Persist the pre-battle spot so the round-trip resumes here (spawn rule (b)).
    this.persistPos();
    this.dialog?.setVisible(false);
    this.showBanner('A friendly spar!');
    this.time.delayedCall(this.reduced ? 0 : 320, () => {
      if (!this.scene.isActive()) return;
      goTo(this, 'Battle', {
        data: {
          kind: 'friendly',
          opponentParty: party,
          opponentName: trainerName,
          returnScene: 'Route',
          returnData: { nodeId: this.nodeId },
        },
      });
    });
  }

  /** Hand off to the Trade scene with the chosen partner (owned by another agent). */
  private openTrade(t: SimTrainer): void {
    this.busy = true;
    this.dialog?.setVisible(false);
    goTo(this, 'Trade', { data: { trainerId: t.id, returnNodeId: this.nodeId } });
  }

  private ensureDialog(): DialogBox {
    if (this.dialog) return this.dialog;
    const safe = this.layout.safe;
    const w = Math.min(560, safe.width - SPACING.lg);
    const h = DIALOG_H;
    this.dialog = new DialogBox(
      this,
      this.layout.centerX,
      safe.y + safe.height - h / 2 - SPACING.md,
      {
        width: w,
        height: h,
        reducedMotion: this.reduced,
      },
    );
    this.dialog.setScrollFactor(0).setDepth(105).setVisible(false);
    return this.dialog;
  }

  // ── leaving ────────────────────────────────────────────────────────────────

  private headHome(): void {
    if (this.busy) return;
    this.busy = true;
    const session = peekSession(this);
    void session?.save().catch(() => undefined);
    goTo(this, 'Town');
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave nothing behind that a
   * re-entry would trip over: kill timers/tweens, drop the keyboard keys we bound, and
   * destroy every control + actor (the worldLayer cascade covers most world objects, but
   * the screen-fixed d-pad/buttons + the followers live outside it). resetState() then
   * re-empties the arrays at the next create().
   */
  private teardown(): void {
    this.tweens.killAll();
    this.time.removeAllEvents();
    // Drop the keys + key listeners (Space/Enter interact) we bound this visit.
    this.input.keyboard?.removeAllKeys(true);
    this.input.keyboard?.off('keydown-SPACE');
    this.input.keyboard?.off('keydown-ENTER');
    this.cursors = undefined;
    this.wasd = undefined;

    // Screen-fixed controls (outside the worldLayer cascade).
    this.dpad?.destroy();
    this.interactButton?.destroy();
    this.homeButton?.destroy();
    this.banner?.destroy();
    this.dialog?.destroy();
    this.greetMenu?.destroy();

    // Seam signposts live in the worldLayer (destroyed with the scene), but drop their
    // input listeners + refs explicitly so a re-entry never trips over a stale handler.
    for (const s of this.seams) {
      for (const o of s.objects) o.removeAllListeners?.();
    }
    this.seams = [];

    // Followers (player + NPCs) live in the worldLayer, which is destroyed with the
    // scene — but null the refs so nothing dangling is touched before reset.
    this.follower = undefined;

    this.dpad = undefined;
    this.actors = [];
    this.followerTrail = [];
    this.interactButton = undefined;
    this.homeButton = undefined;
    this.banner = undefined;
    this.dialog = undefined;
    this.greetMenu = undefined;
    this.nearActor = null;
  }

  // ── banner + layout ──────────────────────────────────────────────────────────

  private showBanner(text: string): void {
    if (!this.banner) return;
    this.banner.setText(text).setAlpha(1);
    // Re-seat now that the text (and thus the measured width) is known, so a wide biome
    // name drops below the Head home button instead of overlapping it on a narrow phone.
    this.positionBanner();
    if (this.reduced) {
      this.time.delayedCall(1400, () => this.banner?.setAlpha(0));
      return;
    }
    this.tweens.killTweensOf(this.banner);
    this.banner.setAlpha(1);
    this.tweens.add({
      targets: this.banner,
      alpha: 0,
      delay: 1100,
      duration: 600,
      ease: 'Sine.easeIn',
    });
  }

  /**
   * After the biome-name banner settles, breathe a quiet rumor of the legendary
   * roaming the region — naming it and the biome it is drifting toward, so the player
   * knows where to chase it. Ambient wonder, never a quest: it shows whether or not
   * the legendary actually surfaced here today (a rumor stands on its own).
   */
  private surfaceRoamingRumor(): void {
    const rumor = this.roamingRumor;
    if (!rumor) return;
    const where = ROAMING_BIOME_PHRASE[rumor.kind] ?? 'somewhere out there';
    const line = `Whispers say ${rumor.name} is drifting toward ${where}…`;
    // Wait out the biome-name banner so the two don't stomp each other.
    this.time.delayedCall(this.reduced ? 1500 : 2200, () => {
      if (!this.scene.isActive() || this.busy) return;
      this.showBanner(line);
    });
  }

  /**
   * Seat the biome banner so it never overlaps the top-right Head home button. The banner
   * is center-anchored at the top; on a narrow phone a wide biome name would reach the
   * button, so it drops into its own clear band BELOW the button row (they stack instead
   * of colliding). On a roomy desktop the centered banner stays well clear at the top.
   * Re-derived from the LIVE measured banner width + the home button's live geometry, so
   * it stays correct across a resize/rotation and once the biome text lands.
   */
  private positionBanner(): void {
    if (!this.banner) return;
    const safe = this.layout.safe;
    const homeW = this.homeButton?.buttonWidth ?? 148;
    const homeH = this.homeButton?.buttonHeight ?? 48;
    const homeLeft = safe.x + safe.width - homeW - SPACING.md;
    const homeTop = safe.y + SPACING.md;
    const bannerW = this.banner.text ? this.banner.displayWidth : 0;
    const top = routeBannerTop({
      centerX: this.layout.centerX,
      bannerWidth: bannerW,
      topY: homeTop,
      buttonLeft: homeLeft,
      buttonBottom: homeTop + homeH,
      margin: SPACING.sm,
      dropGap: SPACING.sm,
    });
    this.banner.setPosition(this.layout.centerX, top);
  }

  private relayout(): void {
    const safe = this.layout.safe;
    this.drawGround();

    // A resize/rotation may have crossed the compact breakpoint (desktop window ↔ phone,
    // or portrait ↔ landscape). Re-derive the footprint multiplier and, if it actually
    // changed, re-fit every live sprite + re-seat its label so the route stays clean.
    const prevScale = this.spriteScale;
    this.refreshSpriteScale();
    if (this.spriteScale !== prevScale) this.rescaleActors();

    // Head-home: top-right, always reachable. Positioned FIRST so its column feeds the
    // biome banner's anti-overlap (positionBanner reads the home button's live geometry).
    const homeW = this.homeButton?.buttonWidth ?? 148;
    const homeH = this.homeButton?.buttonHeight ?? 48;
    this.homeButton?.setPosition(
      safe.x + safe.width - homeW / 2 - SPACING.md,
      safe.y + homeH / 2 + SPACING.md,
    );

    // Biome banner: centered at the top, dropped below Head home on a narrow phone where a
    // wide name would otherwise overlap the button (see positionBanner).
    this.positionBanner();

    // D-pad: bottom-left cross. Touch-first, thumb-reachable (the shared component).
    this.dpad?.layout(safe);

    // Interact button: bottom-right, above the home control's column.
    const iW = this.interactButton?.buttonWidth ?? 132;
    const iH = this.interactButton?.buttonHeight ?? 48;
    this.interactButton?.setPosition(
      safe.x + safe.width - iW / 2 - SPACING.md,
      safe.y + safe.height - iH / 2 - SPACING.md,
    );

    // Re-anchor the dialog + its choice menu if a greeting is in progress.
    if (this.dialog) {
      this.dialog.setPosition(this.layout.centerX, safe.y + safe.height - 58 - SPACING.md);
    }
    this.positionGreetMenu();
  }

  /**
   * Re-fit every live overworld sprite to the new responsive footprint and re-derive its
   * floating-label offset — run only when a resize/rotation actually crossed the compact
   * breakpoint (desktop window ↔ phone, portrait ↔ landscape), so the route never keeps
   * desktop-huge sprites in a phone view (or vice-versa). Each follower stays sized off
   * its OWN leader's now-responsive footprint, so the height ratio + 1.2× cap still hold.
   */
  private rescaleActors(): void {
    // Player trainer + their lead-Pokemon follower.
    if (this.playerAvatar?.sprite.active) fitSprite(this.playerAvatar.sprite, this.playerSide);
    if (this.follower) {
      fitOverworldActor(this.follower, heightScaledSide(this.playerSide, this.followerHeight), 4);
    }

    for (const a of this.actors) {
      if (a.kind === 'wild') {
        const side = heightScaledSide(this.wildSide, a.heightMeters);
        if (a.actor) fitOverworldActor(a.actor, side, 4);
        a.labelOffsetY = labelOffsetFor(WILD_LABEL_OFFSET_Y, WILD_CREATURE_SIDE, side);
      } else {
        if (a.avatar?.sprite.active) fitSprite(a.avatar.sprite, this.npcSide);
        if (a.follower) {
          fitOverworldActor(a.follower, heightScaledSide(this.npcSide, a.followerHeight), 4);
        }
        a.labelOffsetY = labelOffsetFor(TRAINER_LABEL_OFFSET_Y, NPC_TRAINER_SIDE, this.npcSide);
      }
      // Re-seat the label at its (possibly new) lift above the actor's current spot.
      a.label.setPosition(a.x, a.y + a.labelOffsetY);
    }
  }

  /** Soft biome-tinted ground with a few calm decorative tufts (placeholder art). */
  private drawGround(): void {
    if (!this.ground) return;
    const g = this.ground;
    g.clear();
    // Base biome fill across the whole world rect.
    g.fillStyle(this.biome.ground, 1);
    g.fillRect(0, 0, WORLD_W, WORLD_H);

    // A soft meandering path so the route reads as walkable (purely decorative).
    g.fillStyle(0x000000, 0.06);
    for (let i = 0; i < 6; i++) {
      const y = (WORLD_H / 6) * i + 40;
      g.fillRoundedRect(80, y, WORLD_W - 160, 26, RADIUS.lg);
    }

    // A soft ground path leading off each seam edge, so an exit reads as walkable-toward
    // (a gentle lighter band in the drawGround style; the cream signpost sits atop it).
    g.fillStyle(0xffffff, 0.14);
    const bandLen = 220;
    const bandThick = 150;
    for (const s of this.seams) {
      switch (s.link.edge) {
        case 'up':
          g.fillRoundedRect(WORLD_W / 2 - bandThick / 2, 0, bandThick, bandLen, RADIUS.lg);
          break;
        case 'down':
          g.fillRoundedRect(
            WORLD_W / 2 - bandThick / 2,
            WORLD_H - bandLen,
            bandThick,
            bandLen,
            RADIUS.lg,
          );
          break;
        case 'left':
          g.fillRoundedRect(0, WORLD_H / 2 - bandThick / 2, bandLen, bandThick, RADIUS.lg);
          break;
        case 'right':
          g.fillRoundedRect(
            WORLD_W - bandLen,
            WORLD_H / 2 - bandThick / 2,
            bandLen,
            bandThick,
            RADIUS.lg,
          );
          break;
      }
    }

    // Scattered tufts/rocks (deterministic from the node so they don't flicker).
    const rng = this.session.expeditionRng(`${this.nodeId}:decor`);
    g.fillStyle(this.biome.accent, 0.18);
    for (let i = 0; i < 40; i++) {
      const x = 40 + rng.float() * (WORLD_W - 80);
      const y = 40 + rng.float() * (WORLD_H - 80);
      const r = 6 + rng.float() * 10;
      g.fillCircle(x, y, r);
    }
  }
}

/** Fit a loaded sprite into a target side length without distortion. */
function fitSprite(img: Phaser.GameObjects.Image, maxSide: number): void {
  const w = img.width || 1;
  const h = img.height || 1;
  const scale = Math.min(maxSide / w, maxSide / h, 4);
  img.setScale(scale);
}

/** A warm, in-character greeting line (template floor; never toxic). */
function greetingLine(t: SimTrainer): string {
  const style = t.playStyle;
  const byStyle: Record<string, string> = {
    collector: 'Out filling the dex too? This route has some lovely surprises.',
    battler: 'Fancy a gentle spar? No stakes — just for the joy of it.',
    breeder: "I'm scouting for egg-move parents. Want to swap notes?",
    explorer: 'I love this path at this hour. Glad to share it with you.',
    socializer: 'So good to run into a friendly face out here!',
  };
  return byStyle[style] ?? 'Lovely day for a wander, isn’t it?';
}
